import React, { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import styles from "./studentsTable.module.css";
import ZeroData from "../../zeroData/zeroData";
import Badge from "../../badge/badge";
import SearchIcon from "../../../assets/icons/search-icon.svg";

const PAGE_SIZE = 10;

const STATUS_COLOR = {
  Ativo: "green",
  Inativo: "red"
};

// Rótulo do bloco no treemap "Alunos por Status" → valor do select desta tabela.
export const STATUS_FILTER = { Ativo: "ativo", Inativo: "inativo", "Sem status": "sem-status" };

// Status vazio = aluno que só aparece em Academic/Turnover, sem cadastro em Student.
export const matchesStudentFilter = (student, status, term) => {
  const current = student.studentStatus || "";
  if (status === "ativo" && current !== "Ativo") {
    return false;
  }
  if (status === "inativo" && current !== "Inativo") {
    return false;
  }
  if (status === "sem-status" && current !== "") {
    return false;
  }
  if (!term) {
    return true;
  }
  return (
    String(student.studentId).includes(term) ||
    (student.courseName || "").toLowerCase().includes(term)
  );
};

// Mesmas colunas da tabela, com os cabeçalhos que vão para o Excel.
export const toExportRows = (students) =>
  students.map((s) => ({
    "Id do Aluno": s.studentId,
    Status: s.studentStatus || "Sem status",
    Curso: s.courseName || "",
    Disciplinas: s.totalDisciplines,
    "Nota Média": s.avgGrade,
    "Frequência Média": s.avgFrequency,
    "Cidade/UF": [s.city, s.state].filter(Boolean).join("/")
  }));

// Lista dos alunos que compõem o "Total de Alunos Matriculados".
// Busca e paginação client-side: a lista já vem inteira no payload do KPI acadêmico.
// status/onStatusChange são controlados pelo pai (treemap "Alunos por Status" clica aqui).
const StudentsTable = ({ studentData = [], status = "ativo", onStatusChange }) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return studentData.filter((s) => matchesStudentFilter(s, status, term));
  }, [studentData, searchTerm, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [searchTerm, status]);

  const rows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  if (!studentData.length) {
    return (
      <ZeroData>
        <h4>Nenhum aluno matriculado no período.</h4>
      </ZeroData>
    );
  }

  const decimal = (v) => (v === null || v === undefined ? "-" : Number(v).toFixed(2));

  // Exporta o resultado filtrado inteiro, não só a página atual.
  const exportExcel = () => {
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(toExportRows(filtered)), "Alunos");
    XLSX.writeFile(book, "alunos-matriculados.xlsx");
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Alunos Matriculados</h2>
          <span className={styles.subtitle}>{filtered.length} aluno(s)</span>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.searchWrapper}>
            <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por ID ou curso..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <select
            className={styles.orderWrapper}
            value={status}
            onChange={(e) => onStatusChange?.(e.target.value)}
          >
            <option value="todos">Todos os status</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
            <option value="sem-status">Sem status</option>
          </select>

          <button
            type="button"
            className={styles.orderWrapper}
            onClick={exportExcel}
            disabled={!filtered.length}
          >
            Exportar Excel
          </button>
        </div>
      </div>

      {!filtered.length ? (
        <ZeroData>
          <h4>Nenhum aluno para esse filtro.</h4>
        </ZeroData>
      ) : (
        <>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Id do Aluno</th>
                  <th>Status</th>
                  <th>Curso</th>
                  <th>Disciplinas</th>
                  <th>Nota Média</th>
                  <th>Frequência Média</th>
                  <th>Cidade/UF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s, idx) => (
                  <tr key={idx}>
                    <td>{s.studentId}</td>
                    <td>
                      <Badge
                        text={s.studentStatus || "Sem status"}
                        color={STATUS_COLOR[s.studentStatus] || "blue"}
                      />
                    </td>
                    <td>{s.courseName || "-"}</td>
                    <td>{s.totalDisciplines}</td>
                    <td>{decimal(s.avgGrade)}</td>
                    <td>{decimal(s.avgFrequency)}</td>
                    <td>{[s.city, s.state].filter(Boolean).join("/") || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </button>
              <span className={styles.pageInfo}>
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentsTable;
